/**
 * LoginPage Component
 *
 * Landing page for unauthenticated users.
 * Displays a Microsoft sign-in button.
 */

import { Spinner } from '@medsuite/ui-components';
import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../providers/AuthProvider';

/**
 * Microsoft logo SVG
 */
function MicrosoftLogo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * LoginPage component
 */
export function LoginPage(): React.ReactElement {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Get the page the user was trying to access
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  // Redirect to original destination if already authenticated
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  /**
   * Handle sign in button click
   */
  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await login();
    } catch (error) {
      console.error('Sign in error:', error);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4">
      {/* Logo and title */}
      <div className="mb-8 text-center">
        <img
          src="/assets/medsuite-logo.png"
          alt="Product Governance Logo"
          className="mx-auto mb-4 h-[100px] w-auto"
        />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Product Governance</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Strategic leadership through people, process and technology
        </p>
      </div>

      {/* Login card */}
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 p-8 shadow-xl">
        <h2 className="mb-6 text-center text-xl font-semibold text-gray-900 dark:text-gray-100">
          Sign in to continue
        </h2>

        {/* Microsoft sign-in button */}
        <button
          onClick={handleSignIn}
          disabled={isSigningIn}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 text-gray-700 dark:text-gray-200 shadow-sm transition-all hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSigningIn ? (
            <>
              <Spinner size="sm" />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <MicrosoftLogo />
              <span className="font-medium">Sign in with Microsoft</span>
            </>
          )}
        </button>

        {/* Info text */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Use your organizational Microsoft account to sign in.
          <br />
          MFA may be required based on your organization&apos;s policies.
        </p>
      </div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-gray-500 dark:text-gray-500">
        Protected by Microsoft Entra ID
      </p>
    </div>
  );
}

export default LoginPage;
